import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useContext, useEffect, useState, useRef } from 'react'
import { Breadcrumbs, Typography, LinearProgress, Button } from '@mui/material'

import '../styles/users-management.css'
import { tablesHeaders } from '../utils/db'
import '../styles/customers-management.css'
import Dialog from '../components/common/dialog'
import { requestAccessToken } from '../api/auth.api'
import HelpIcon from '../assets/icons/help-icon.svg'
import Pagination from '../components/common/pagination'
import ActionsBar from '../components/common/users-actions-bar'
import { baseButtonBlueBg } from '../theme/custom-styles'
import { BankAccount, Customer, FormError } from '../types'
import { FiltersOptionsModal } from '../components/common/filters-options-modal'
import CustomerDataForm from '../components/customers-management/customer-data-form'
import { ProcessingDocumentWarning } from '../components/common/processing-document-warning'
import CustomerDataListItem from '../components/customers-management/customer-data-list-item'
import { createCustomer, deleteCustomerData } from '../api/customers.api'
import { AppStateContext, AuthContext, NotificationsContext, UserContext } from '../contexts'
import { handleSetDataToTable, handleDeleteDataByOne } from '../utils/db/common-methods'
import { handleSyncDataToLocalDB, normalizeCustomerData } from '../utils'
import { searchCustomersByRegex } from '../api/app-queries.api'
import { MexicanCustomerPaymentForm } from '../components/customer-companies-management/mexican-customer-payment-form'

const CustomersManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'

  const navigate = useNavigate()
  const { userSession } = useContext(UserContext)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const { t } = useTranslation(['customers-management', 'customer-company-details-management', 'common'])
  const { banksData, processingBatch, setScrollToTop, customersBriefData, defaultPocketIdSelected, setCustomersBriefData } = useContext(AppStateContext)

  const currentListPageRef = useRef<number>(1)
  const customerToSearchRef = useRef<string>('')
  const currentLoadingDataRef = useRef<boolean>(true)
  const processingDocumentRef = useRef<boolean>(false)
  const customersToDeleteRef = useRef<Array<string>>([])
  const noRegistriesFoundAtDatabaseRef = useRef<boolean>(false)
  const currentCustomersDataRef = useRef<Array<Partial<Customer>>>([])
  const currentShowDeleteButtonFromActionsBarRef = useRef<boolean>(false)

  const [errors, setErrors] = useState<FormError>({})
  const [pageSize, setPageSize] = useState<number>(50)
  const [currentListPage, setCurrentListPage] = useState<number>(1)
  const [selectedPocketId, setSelectedPocketId] = useState<string>('')
  const [customerToSearch, setCustomerToSearch] = useState<string>('')
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [selectAllCustomers, setSelectAllCustomers] = useState<boolean>(false)
  const [customersToDelete, setCustomersToDelete] = useState<Array<string>>([])
  const [searchingInDatabase, setSearchingInDatabase] = useState<boolean>(false)
  const [openNewCustomerModal, setOpenNewCustomerModal] = useState<boolean>(false)
  const [customersList, setCustomersList] = useState<Array<Partial<Customer>>>([])
  const [customersData, setCustomersData] = useState<Array<Partial<Customer>>>([])
  const [errorInDeletionProcess, setErrorInDeletionProcess] = useState<boolean>(false)
  const [openFiltersOptionsModal, setOpenFiltersOptionsModal] = useState<boolean>(false)
  const [openDeleteCustomerModal, setOpenDeleteCustomerModal] = useState<boolean>(false)
  const [customerSelectedData, setCustomerSelectedData] = useState<Partial<Customer>>({})
  const [searchedCustomers, setSearchedCustomers] = useState<Array<Partial<Customer>>>([])
  const [dialogNewCustomerIsLoading, setDialogNewCustomerIsLoading] = useState<boolean>(false)
  const [noRegistriesFoundAtLocal, setNoRegistriesFoundAtLocal] = useState<boolean>(false)
  const [noRegistriesFoundAtDatabase, setNoRegistriesFoundAtDatabase] = useState<boolean>(false)
  const [dialogDeleteCustomerIsLoading, setDialogDeleteCustomerIsLoading] = useState<boolean>(false)
  const [showDeleteButtonFromActionsBar, setShowDeleteButtonFromActionsBar] = useState<boolean>(false)

  useEffect(() => {
    if (processingBatch) {
      processingDocumentRef.current = true
    } else {
      processingDocumentRef.current = false
    }
  }, [processingBatch])

  useEffect(() => {
    if (customersBriefData) {
      handleSetCustomersByCountryInitialData(customersBriefData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customersBriefData])

  useEffect(() => {
    if (customersToDelete.length === 0) {
      currentShowDeleteButtonFromActionsBarRef.current = false
      setShowDeleteButtonFromActionsBar(false)
    } else {
      if (!customerSelectedData.registeredFullName) {
        currentShowDeleteButtonFromActionsBarRef.current = true
        setShowDeleteButtonFromActionsBar(true)
      }
    }
  }, [customersToDelete, customerSelectedData])

  useEffect(() => {
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const customersPerPage = customerToSearch === '' ? customersData.slice(firstPageIndex, lastPageIndex) : searchedCustomers.slice(firstPageIndex, lastPageIndex)

    setCustomersList(customersPerPage)

    if (processingDocumentRef.current !== true) {
      for (const element of customersPerPage) {
        const customerToAdd = normalizeCustomerData(element)
        handleSyncDataToLocalDB('customers', customerToAdd)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentListPage, customersData, searchedCustomers, pageSize])

  const handleGetCustomerBelongsTo = () => {
    if (userSession?.role.includes('client')) {
      return userSession.belongsTo
    }

    return 'holdings'
  }

  const handleSetCustomersByCountryInitialData = async (dataToSet: any) => {
    if (dataToSet.customersData) {
      if (dataToSet.customersData.length === 0 && currentLoadingDataRef.current === true) {
        currentLoadingDataRef.current = false
        currentCustomersDataRef.current = []
        setLoadingLocalData(false)
        return
      }

      const currentSelectedCustomersIds =
        currentCustomersDataRef.current.length === 0 ? [] : currentCustomersDataRef.current.filter((customerData) => customerData.selected === true).map((customerData) => String(customerData._id))
      const currentListPageDataState = currentListPageRef.current
      const currentCustomerToSearchState = customerToSearchRef.current

      const customersNormalizedData = dataToSet.customersData.map((customerData: Customer) => {
        if (currentSelectedCustomersIds.includes(String(customerData._id))) {
          return { ...normalizeCustomerData(customerData), selected: true }
        }

        return { ...normalizeCustomerData(customerData), selected: false }
      })

      customersNormalizedData.sort((thisCustomer: Customer, nextCustomer: Customer) => (thisCustomer?.registeredFullName || '').toLowerCase().localeCompare((nextCustomer?.registeredFullName || '').toLowerCase()))

      if (currentCustomerToSearchState === '' || currentCustomerToSearchState === null) {
        const firstPageIndex = currentListPageDataState === null ? 0 : (currentListPageDataState - 1) * pageSize
        const lastPageIndex = firstPageIndex + pageSize
        const customersPerPage = customersNormalizedData.slice(firstPageIndex, lastPageIndex)

        handleCustomersDeletionProcess(customersPerPage)
        setCustomersList(customersPerPage as Array<Customer>)

        if (processingDocumentRef.current !== true) {
          for (const element of customersPerPage) {
            const customerToAdd = normalizeCustomerData(element)
            handleSyncDataToLocalDB('customers', customerToAdd)
          }
        }
      }

      currentCustomersDataRef.current = customersNormalizedData
      setCustomersData(customersNormalizedData as Array<Customer>)

      if (currentLoadingDataRef.current === true) {
        currentLoadingDataRef.current = false
        setLoadingLocalData(false)
      }
    }
  }

  const handleCustomersDeletionProcess = (customersPerPage: Array<Customer>) => {
    const currentCustomersToDeleteState = customersToDeleteRef.current ?? []
    const currentShowDeleteButtonFromActionsBarState = currentShowDeleteButtonFromActionsBarRef.current
    const customersToDeleteOutOfPage = []

    for (const element of currentCustomersToDeleteState) {
      const customerToDeleteId = element
      const findCustomerIndex = customersPerPage?.findIndex((customerData) => String(customerData._id) === String(customerToDeleteId))

      if (findCustomerIndex === -1) {
        customersToDeleteOutOfPage.push(customerToDeleteId)
      }
    }

    for (const element of customersToDeleteOutOfPage) {
      const customerId = element
      const index = currentCustomersToDeleteState.indexOf(customerId)
      if (index > -1) {
        currentCustomersToDeleteState.splice(index, 1)
      }
    }

    customersToDeleteRef.current = currentCustomersToDeleteState
    if (customersToDeleteRef.current.length < pageSize) {
      setSelectAllCustomers(false)
    }

    if (customersToDeleteRef.current.length === 0) {
      setShowDeleteButtonFromActionsBar(false)
    } else {
      setShowDeleteButtonFromActionsBar(currentShowDeleteButtonFromActionsBarState)
    }

    setTimeout(() => {
      setCustomersToDelete(currentCustomersToDeleteState)
    }, 50)
  }

  const handleUncheckAllCustomers = () => {
    setSelectAllCustomers(false)
    setCustomersData((currentCustomersState) => {
      const customersStateUpdated = [...currentCustomersState]
      const customersStateUpdatedToReturn = customersStateUpdated.map((customerData) => ({
        ...customerData,
        selected: false
      }))

      currentCustomersDataRef.current = customersStateUpdatedToReturn
      return customersStateUpdatedToReturn
    })
  }

  const handleSetCustomerToSearch = (customerToSearchData: string, showError: boolean) => {
    setNoRegistriesFoundAtLocal(false)
    currentListPageRef.current = 1
    customerToSearchRef.current = customerToSearchData

    if (noRegistriesFoundAtDatabaseRef.current === true) {
      setNoRegistriesFoundAtDatabase(false)
    }

    setErrors({})
    setCurrentListPage(1)
    setCustomerToSearch(customerToSearchData)

    if (customerToSearchData === '') {
      const firstPageIndex = (currentListPage - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const currentCustomersDataState = currentCustomersDataRef.current ?? []
      const customersPerPage = currentCustomersDataState.slice(firstPageIndex, lastPageIndex)

      setCustomersList(customersPerPage)
      setSearchedCustomers([])

      if (showError) setErrors({ userToSearch: t('searchField.errors.emptyField') })
      return
    }

    const currentFullCustomersData = [...(currentCustomersDataRef.current as Array<Customer>)]
    const filteredCustomers = currentFullCustomersData.filter((customer) => {
      const customerRegisteredFullName = customer.registeredFullName ? customer.registeredFullName : ''
      const customerMainBankAccount =
        Array.isArray(customer.bankAccountsData) && customer.bankAccountsData.length > 0 ? customer.bankAccountsData.find((bankAccountData: BankAccount) => bankAccountData.bankAccountMain === true) : null
      const customerDocumentNumber = customer.documentNumber ? customer.documentNumber : ''
      const customerMainBankName = customerMainBankAccount ? customerMainBankAccount.bankName : ''
      const customerMainBankAccountNumber = customerMainBankAccount ? customerMainBankAccount.bankNumber : ''

      if (customer.selected === true) customer.selected = false

      return (
        customerRegisteredFullName.toLowerCase().includes(customerToSearchData.toLowerCase()) ||
        customerDocumentNumber.toLowerCase().includes(customerToSearchData.toLowerCase()) ||
        customerMainBankName?.toLowerCase().includes(customerToSearchData.toLowerCase()) ||
        customerMainBankAccountNumber.toLowerCase().includes(customerToSearchData.toLowerCase())
      )
    })

    customersToDeleteRef.current = []
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const customersPerPage = filteredCustomers.slice(firstPageIndex, lastPageIndex)

    setCustomersToDelete([])
    handleUncheckAllCustomers()
    setSelectAllCustomers(false)
    setCustomersList(customersPerPage)
    setSearchedCustomers(filteredCustomers)

    if (filteredCustomers.length === 0) {
      setNoRegistriesFoundAtLocal(true)
    }
  }

  const handleValidateNewCustomerForm = async () => {
    setErrors({})
    let formDataIsValid = true
    const formErrors: FormError = {}

    if (!customerSelectedData.firstName) {
      formDataIsValid = false
      formErrors.firstName = t('newCustomerModal.customerDataForm.errors.emptyFirstName')
    }

    if (!customerSelectedData.lastName) {
      formDataIsValid = false
      formErrors.lastName = t('newCustomerModal.customerDataForm.errors.emptyLastName')
    }

    if (!customerSelectedData.documentType) {
      formDataIsValid = false
      formErrors.documentType = t('newCustomerModal.customerDataForm.errors.emptyDocumentType')
    }

    if (!customerSelectedData.documentNumber) {
      formDataIsValid = false
      formErrors.documentNumber = t('newCustomerModal.customerDataForm.errors.emptyDocumentNumber')
    }

    if (selectedCountry === 'COL') {
      if (
        !Array.isArray(customerSelectedData.bankAccountsData) ||
        (Array.isArray(customerSelectedData.bankAccountsData) && customerSelectedData.bankAccountsData.length > 0 && customerSelectedData.bankAccountsData[0] && !customerSelectedData.bankAccountsData[0].bankAccountType)
      ) {
        formDataIsValid = false
        formErrors.bankAccountType = t('newCustomerModal.customerDataForm.errors.emptyBankAccountType')
      }

      if (
        !Array.isArray(customerSelectedData.bankAccountsData) ||
        (Array.isArray(customerSelectedData.bankAccountsData) &&
          customerSelectedData.bankAccountsData.length > 0 &&
          customerSelectedData.bankAccountsData[0] &&
          customerSelectedData?.bankAccountsData[0].bankAccountType !== 'breb' &&
          !customerSelectedData.bankAccountsData[0].bankName)
      ) {
        formDataIsValid = false
        formErrors.bankName = t('newCustomerModal.customerDataForm.errors.emptyBankName')
      }

      if (
        !Array.isArray(customerSelectedData.bankAccountsData) ||
        (Array.isArray(customerSelectedData.bankAccountsData) && customerSelectedData.bankAccountsData.length > 0 && customerSelectedData.bankAccountsData[0] && !customerSelectedData.bankAccountsData[0].bankNumber)
      ) {
        formDataIsValid = false
        formErrors.bankAccountNumber = t('newCustomerModal.customerDataForm.errors.emptyBankAccountNumber')
      }
    }

    if (selectedCountry === 'MEX') {
      if (
        !Array.isArray(customerSelectedData?.bankAccountsData) ||
        (Array.isArray(customerSelectedData?.bankAccountsData) &&
          customerSelectedData?.bankAccountsData.length > 0 &&
          customerSelectedData?.bankAccountsData[0] &&
          !customerSelectedData?.bankAccountsData[0].bankAccountType)
      ) {
        formDataIsValid = false
        formErrors.bankAccountType = t('newCustomerModal.customerDataForm.errors.emptyBankAccountType')
      }

      if (
        !Array.isArray(customerSelectedData?.bankAccountsData) ||
        (Array.isArray(customerSelectedData?.bankAccountsData) && customerSelectedData?.bankAccountsData.length > 0 && customerSelectedData?.bankAccountsData[0] && !customerSelectedData?.bankAccountsData[0].bankNumber)
      ) {
        formDataIsValid = false
        formErrors.bankAccountNumber = t('newCustomerModal.customerDataForm.errors.emptyBankAccountNumber')
      }
    }

    if (userSession?.role === 'super-admin-client' && !selectedPocketId) {
      formDataIsValid = false
      formErrors.selectedPocketId = t('newCustomerModal.customerDataForm.errors.emptySelectedPocket', { ns: 'customers-management' })
    }

    console.log('formDataIsValid', formDataIsValid, 'selectedPocketId', selectedPocketId)

    if (!formDataIsValid) {
      setErrors(formErrors)
      return false
    }

    const selectedPocketIdToSet =
      userSession?.role === 'super-admin-client' ? selectedPocketId : userSession?.role === 'admin-client' || userSession?.role === 'admin-client-builder' ? userSession?.adminOfPockets?.[0] : ''

    try {
      setDialogNewCustomerIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const belongsTo = handleGetCustomerBelongsTo()
      const createdCustomerData = await createCustomer(
        {
          belongsTo,
          country: selectedCountry.toLowerCase(),
          documentType: customerSelectedData.documentType,
          documentNumber: customerSelectedData.documentNumber,
          customerCompanyPocketsRelated: [selectedPocketIdToSet || ''],
          registeredFullName: `${customerSelectedData.firstName} ${customerSelectedData.lastName}`,
          bankAccountsData: Array.isArray(customerSelectedData.bankAccountsData) && customerSelectedData.bankAccountsData.length > 0 ? customerSelectedData.bankAccountsData[0] : undefined
        },
        accessToken
      )

      await handleSetDataToTable('customers', tablesHeaders['customers'].join(','), createdCustomerData.data)

      setOpenNewCustomerModal(false)
      setDialogNewCustomerIsLoading(false)
      setCustomerSelectedData({})
      updateNewNotificationStatus?.(true, 'customer-created')
    } catch (error: any) {
      console.log('Error handleValidateNewCustomerForm', error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401 && error?.response?.data.message === 'Not authorized: Exists') {
        setErrors({ generalError: `${t('formGeneralErrors.unauthorized', { ns: 'common' })}: 406` })
      } else if (error?.response?.status === 401 && !(error?.response?.data.message === 'Not authorized: Exists')) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogNewCustomerIsLoading(false)
    }
  }

  const handleConfirmCustomerDeletion = async () => {
    setErrors({})
    setDialogDeleteCustomerIsLoading(true)
    const currentCustomersToDelete = [...customersToDeleteRef.current]

    if (customerSelectedData.registeredFullName && !customersToDeleteRef.current?.includes(String(customerSelectedData._id))) {
      customersToDeleteRef.current = [String(customerSelectedData._id)]
      setCustomersToDelete([String(customerSelectedData._id)])
      currentCustomersToDelete.push(String(customerSelectedData._id))
    }

    for (const element of currentCustomersToDelete) {
      const customerToDeleteId = element

      if (processingDocumentRef.current === true) {
        handleDiscardDeletionProcess()
        setOpenDeleteCustomerModal(false)
        updateNewNotificationStatus?.(true, 'deletion-process-interrupted')
        break
      }

      try {
        const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
        await deleteCustomerData(customerToDeleteId, accessTokenData.data.accessToken)
        await handleDeleteDataByOne('customers', customerToDeleteId)
      } catch (error: any) {
        console.log(error, error?.response?.data?.message)
        if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
          handleCloseSession()
        } else if (error?.response?.status === 401) {
          setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
        } else {
          setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
        }

        // Es necesario dejar esto aquí
        handleDiscardDeletionProcess()
        setErrorInDeletionProcess(true)
        return
      }
    }

    handleDiscardDeletionProcess()
    setOpenDeleteCustomerModal(false)
    updateNewNotificationStatus?.(true, 'customer-deleted')
  }

  const handleDiscardDeletionProcess = () => {
    setCustomersToDelete([])
    setCustomerSelectedData({})
    handleUncheckAllCustomers()
    customersToDeleteRef.current = []
    setDialogDeleteCustomerIsLoading(false)
    const currentCustomerToSearchState = customerToSearchRef.current

    if (currentCustomerToSearchState !== '') {
      handleSetCustomerToSearch('', false)
    }
  }

  const handleSearchCustomerDataByRegex = async () => {
    try {
      setErrors({})
      setSearchingInDatabase(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))

      const { data } = await searchCustomersByRegex({ searchText: customerToSearch, belongsTo: userSession?.belongsTo, selectedCountry, customerCompanyPocketId: defaultPocketIdSelected }, accessToken)
      const customerFindings = data.customersData

      if (customerFindings.length === 0) {
        setSearchingInDatabase(false)
        setNoRegistriesFoundAtDatabase(true)
        noRegistriesFoundAtDatabaseRef.current = true
        return
      }

      const customersDataToAddToList = []
      const currentSearchedCustomers = [...searchedCustomers]
      const currentCustomersData = [...(currentCustomersDataRef.current as Array<Customer>)]

      for (const element of customerFindings) {
        const customerFindingData = element
        const customerAlreadyAdded = searchedCustomers.find((customerData) => String(customerData._id) === String(customerFindingData._id))

        if (!customerAlreadyAdded) customersDataToAddToList.push({ ...customerFindingData, selected: false })
      }

      const customerListDataUpdated = currentSearchedCustomers.concat(customersDataToAddToList)
      customerListDataUpdated.sort((thisCustomer: Partial<Customer>, nextCustomer: Partial<Customer>) =>
        (thisCustomer?.registeredFullName || '').toLowerCase().localeCompare((nextCustomer?.registeredFullName || '').toLowerCase())
      )
      const customersSearchedUpdated = customerListDataUpdated.map((customerData) => ({
        ...customerData,
        selected: false
      }))

      const firstPageIndex = (currentListPage - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const customersPerPage = customersSearchedUpdated.slice(firstPageIndex, lastPageIndex)

      const customersDataUpdated = currentCustomersData.concat(customersDataToAddToList)
      customersDataUpdated.sort((thisCustomer: Partial<Customer>, nextCustomer: Partial<Customer>) =>
        (thisCustomer?.registeredFullName || '').toLowerCase().localeCompare((nextCustomer?.registeredFullName || '').toLowerCase())
      )
      const currentCustomersDataUpdated = customersDataUpdated.map((customerData) => ({
        ...customerData,
        selected: false
      }))

      for (const element of customersDataToAddToList) {
        const customerDataToAddToList = element
        delete customerDataToAddToList.selected

        await handleSetDataToTable('customers', tablesHeaders['customers'].join(','), customerDataToAddToList)
      }

      customersToDeleteRef.current = []
      setCustomersToDelete([])

      setCustomersList(customersPerPage)
      currentCustomersDataRef.current = currentCustomersDataUpdated
      setCustomersData(currentCustomersDataUpdated)
      setSearchedCustomers(customersSearchedUpdated)

      setScrollToTop(true)
      customersToDeleteRef.current = []
      handleUncheckAllCustomers()
      setCustomersToDelete([])
      setCustomerSelectedData({})
      setSearchingInDatabase(false)
      currentShowDeleteButtonFromActionsBarRef.current = false
      setShowDeleteButtonFromActionsBar(false)
      setNoRegistriesFoundAtLocal(false)
      setCustomersBriefData({ customersData: currentCustomersDataUpdated })
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setSearchingInDatabase(false)
      return
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <div className="section-container">
      <Breadcrumbs aria-label="breadcrumb">
        <Typography
          key="3"
          className="breadcrumb-active"
        >
          {t('breadcrumbs.firstLevel')}
        </Typography>
      </Breadcrumbs>
      <ActionsBar
        errors={errors}
        hideFilterButton
        userToSearch={customerToSearch}
        userTypeName={t('userTypeName')}
        className="customers-management"
        generalActionChecked={selectAllCustomers}
        disableActionOfButtons={searchingInDatabase}
        handleClearSearchInput={() => setErrors({})}
        handleSetUserToSearch={handleSetCustomerToSearch}
        showDeleteButton={showDeleteButtonFromActionsBar}
        processingDocument={processingDocumentRef.current}
        handleOpenFiltersOptionsModal={() => setOpenFiltersOptionsModal(true)}
        usersData={customerToSearch === '' ? customersData : searchedCustomers}
        handleDialogOpenState={(actionToDo: string) => {
          if (actionToDo === 'delete') {
            setOpenDeleteCustomerModal(!openDeleteCustomerModal)
            return
          }

          const firstPageIndex = (currentListPage - 1) * pageSize
          const lastPageIndex = firstPageIndex + pageSize
          const customersPerPage = customersData.slice(firstPageIndex, lastPageIndex)

          setCustomersList(customersPerPage)
          setOpenNewCustomerModal(!openNewCustomerModal)
        }}
        handleToggleGeneralActionChecked={(checked) => {
          setSelectAllCustomers(checked)

          if (checked === true) {
            const currentCustomersList = [...customersList]
            const currentCustomersDataStateUpdated = currentCustomersDataRef.current
            const customersListUpdated = currentCustomersList.map((customerData) => ({
              ...customerData,
              selected: true
            }))
            const currentCustomersDataIds = customersListUpdated.map((customerData) => customerData._id)
            const filteredCustomersToDelete = currentCustomersDataIds.filter((id) => id !== undefined) as Array<string>
            currentShowDeleteButtonFromActionsBarRef.current = true
            customersToDeleteRef.current = filteredCustomersToDelete

            for (const element of customersListUpdated) {
              const customerListData = element
              const findCustomerIndex = currentCustomersDataStateUpdated?.findIndex((customerData) => String(customerData._id) === String(customerListData._id)) ?? -1

              if (findCustomerIndex !== -1 && currentCustomersDataStateUpdated) {
                currentCustomersDataStateUpdated[findCustomerIndex].selected = true
              }
            }

            currentCustomersDataRef.current = currentCustomersDataStateUpdated

            setCustomersList(customersListUpdated)
            setShowDeleteButtonFromActionsBar(true)
            setCustomersToDelete(filteredCustomersToDelete)
            setCustomersData(currentCustomersDataStateUpdated as Array<Customer>)
          } else {
            const currentCustomersList = [...customersList]
            const currentCustomersDataStateUpdated = currentCustomersDataRef.current
            const customersListUpdated = currentCustomersList.map((customerData) => ({
              ...customerData,
              selected: false
            }))

            for (const element of customersListUpdated) {
              const customerListData = element
              const findCustomerIndex = currentCustomersDataStateUpdated?.findIndex((customerData) => String(customerData._id) === String(customerListData._id)) ?? -1

              if (findCustomerIndex !== -1 && currentCustomersDataStateUpdated) {
                currentCustomersDataStateUpdated[findCustomerIndex].selected = false
              }
            }

            customersToDeleteRef.current = []
            currentShowDeleteButtonFromActionsBarRef.current = false
            currentCustomersDataRef.current = currentCustomersDataStateUpdated

            handleUncheckAllCustomers()
            setCustomersToDelete([])
            setCustomersList(customersListUpdated)
            setShowDeleteButtonFromActionsBar(false)
            setCustomersData(currentCustomersDataStateUpdated as Array<Customer>)
          }
        }}
      />
      {processingBatch && <ProcessingDocumentWarning />}
      <div className="table-header-container">
        <div className="users-management-user-data-profile table-header-text first-table-header">{t('tableHeaders.beneficiary')}</div>
        <div className="users-management-user-data-bank-data-container table-header-text justify-content-end-lg-down">{t('tableHeaders.bankAccountData')}</div>
        <div className="users-management-user-data-actions table-header-text">{t('tableHeaders.actions')}</div>
      </div>
      {loadingLocalData ? (
        <div className="users-management-users-list-container">
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
        </div>
      ) : (
        <>
          <div className="users-management-users-list-container">
            {customersList.map((customerData: Partial<Customer>) => {
              return (
                <CustomerDataListItem
                  key={customerData._id}
                  customerData={customerData}
                  disableActionOfButtons={searchingInDatabase}
                  processingDocument={processingDocumentRef.current}
                  handleToggleCustomerSelection={(selectedCustomerData: Partial<Customer>, selected: boolean) => {
                    let customersSelected = 0
                    let customersToDeleteUpdated = []
                    const currentCustomersList = [...customersList]
                    const currentCustomersToDelete = [...customersToDelete]
                    const currentCustomersDataStateUpdated = currentCustomersDataRef.current

                    const customersListUpdated = currentCustomersList.map((customerData) => {
                      if (String(customerData._id) === String(selectedCustomerData._id)) {
                        customerData.selected = selected
                      }

                      if (customerData.selected === true) customersSelected++

                      return customerData
                    })

                    if (selected === true) {
                      customersToDeleteUpdated = currentCustomersToDelete.concat(selectedCustomerData._id ?? [])
                      customersToDeleteRef.current = customersToDeleteUpdated
                      setCustomersToDelete(customersToDeleteUpdated)

                      const findCustomerIndex = currentCustomersDataStateUpdated?.findIndex((customerData) => String(customerData._id) === String(selectedCustomerData._id)) ?? -1

                      if (findCustomerIndex !== -1 && currentCustomersDataStateUpdated) {
                        currentCustomersDataStateUpdated[findCustomerIndex].selected = true
                      }

                      currentCustomersDataRef.current = currentCustomersDataStateUpdated
                      setCustomersData(currentCustomersDataStateUpdated as Array<Customer>)
                    } else {
                      const index = currentCustomersToDelete.indexOf(selectedCustomerData._id ?? '')
                      if (index > -1) {
                        currentCustomersToDelete.splice(index, 1)
                      }

                      customersToDeleteUpdated = [...currentCustomersToDelete]
                      customersToDeleteRef.current = customersToDeleteUpdated
                      setCustomersToDelete(currentCustomersToDelete)

                      const findCustomerIndex = currentCustomersDataStateUpdated?.findIndex((customerData) => String(customerData._id) === String(selectedCustomerData._id)) ?? -1

                      if (findCustomerIndex !== -1 && currentCustomersDataStateUpdated) {
                        currentCustomersDataStateUpdated[findCustomerIndex].selected = false
                      }

                      currentCustomersDataRef.current = currentCustomersDataStateUpdated
                      setCustomersData(currentCustomersDataStateUpdated as Array<Customer>)
                    }

                    if (customersToDeleteUpdated.length > 0) {
                      currentShowDeleteButtonFromActionsBarRef.current = true
                      setShowDeleteButtonFromActionsBar(true)
                    } else {
                      currentShowDeleteButtonFromActionsBarRef.current = false
                      setShowDeleteButtonFromActionsBar(false)
                    }

                    if (customersList.length === customersSelected) {
                      setSelectAllCustomers(true)
                    } else {
                      setSelectAllCustomers(false)
                    }

                    setCustomersList(customersListUpdated)
                  }}
                  handleOpenDeleteCustomerDataModal={(selectedCustomerData: Partial<Customer>) => {
                    const customersToDeleteUpdated = [selectedCustomerData._id] as Array<string>
                    const currentCustomersList = [...customersList]
                    const customersListUpdated = currentCustomersList.map((customerData) => ({
                      ...customerData,
                      selected: false
                    }))

                    setCustomerSelectedData({
                      ...selectedCustomerData
                    })
                    handleUncheckAllCustomers()
                    setSelectAllCustomers(false)
                    setOpenDeleteCustomerModal(true)
                    setCustomersList(customersListUpdated)
                    setShowDeleteButtonFromActionsBar(false)
                    setCustomersToDelete(customersToDeleteUpdated)

                    customersToDeleteRef.current = customersToDeleteUpdated
                    currentShowDeleteButtonFromActionsBarRef.current = false
                  }}
                  handleGoToUpdateCustomerData={(selectedCustomerData: Partial<Customer>) => {
                    navigate(`/beneficiary-details/${selectedCustomerData._id}`)
                  }}
                />
              )
            })}
          </div>
          <Pagination
            pageSize={pageSize}
            currentPage={currentListPage}
            handlePaginationPageChange={(page: number) => {
              handleUncheckAllCustomers()

              setTimeout(() => {
                setScrollToTop(true)
                setCustomersToDelete([])
                setCurrentListPage(page)
                setSelectAllCustomers(false)

                customersToDeleteRef.current = []
                currentListPageRef.current = page
              }, 100)
            }}
            handleChangePageSize={(pageSizeToSet: string) => {
              setScrollToTop(true)
              setPageSize(Number(pageSizeToSet))
            }}
            totalCount={customerToSearch === '' ? customersData.length : searchedCustomers.length}
          />
          {noRegistriesFoundAtLocal && (
            <div className="customer-no-data-found-container">
              {searchingInDatabase ? (
                <>
                  <div className="customer-no-data-found-searching-progress-bar">
                    <LinearProgress />
                  </div>
                  <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case3')}</div>
                </>
              ) : (
                <>
                  {noRegistriesFoundAtDatabase === false ? (
                    <>
                      <div className="customer-no-data-found-no-registries-text">
                        {t('searchingResultTexts.case1.firstPart')}{' '}
                        <strong
                          className="customer-no-data-found-no-registries-text-click"
                          onClick={() => handleSearchCustomerDataByRegex()}
                        >
                          {t('searchingResultTexts.case1.thirdPart')}
                        </strong>{' '}
                        {t('searchingResultTexts.case1.fourthPart')}
                      </div>
                      <Button
                        sx={baseButtonBlueBg}
                        onClick={handleSearchCustomerDataByRegex}
                      >
                        {t('searchingResultTexts.expandSearch', { ns: 'customer-company-details-management' })}
                      </Button>
                      {errors.generalError && (
                        <div
                          style={{ marginTop: '0px' }}
                          className="error-message"
                        >
                          {errors.generalError}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="customer-no-data-found-no-registries-text">
                        <img
                          src={HelpIcon}
                          alt="help-icon"
                        />
                      </div>
                      <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case2')}</div>
                    </>
                  )}
                </>
              )}
            </div>
          )}
          {openNewCustomerModal && (
            <Dialog
              errors={errors}
              dialogTitle={t('newCustomerModal.title')}
              actionButtonText={t('newCustomerModal.actionButtonText')}
              dialogIsLoading={dialogNewCustomerIsLoading}
              handleDialogAction={handleValidateNewCustomerForm}
              handleDialogOpenState={(openState: boolean) => {
                setOpenNewCustomerModal(openState)

                if (openState === false) {
                  setErrors({})
                  setSelectedPocketId('')
                  setCustomerSelectedData({})
                }
              }}
            >
              {selectedCountry === 'COL' && (
                <CustomerDataForm
                  errors={errors}
                  banksData={banksData ?? []}
                  selectedPocketId={selectedPocketId}
                  customerData={customerSelectedData}
                  handleSetCustomerData={(newCustomerData: Partial<Customer>) => {
                    setErrors({})
                    setCustomerSelectedData((prevCustomerData: Partial<Customer>) => ({
                      ...prevCustomerData,
                      ...newCustomerData
                    }))
                  }}
                  handleSetPocketId={(pocketIdToSet: string) => setSelectedPocketId(pocketIdToSet)}
                />
              )}
              {selectedCountry === 'MEX' && (
                <MexicanCustomerPaymentForm
                  errors={errors}
                  selectedCurrency="MXN"
                  customerData={customerSelectedData}
                  selectedPocketId={selectedPocketId}
                  handleSetPocketId={(pocketIdToSet: string) => setSelectedPocketId(pocketIdToSet)}
                  handleSetCustomerData={(attributeToSet: string, valueToSet: string | Array<BankAccount>) => {
                    setCustomerSelectedData((prevCustomerData: Partial<Customer> | null) => ({
                      ...prevCustomerData,
                      [attributeToSet]: valueToSet
                    }))
                  }}
                />
              )}
            </Dialog>
          )}
          {openDeleteCustomerModal && (
            <Dialog
              errors={errors}
              actionButtonWarningStyle
              hideActionsButtons={errorInDeletionProcess}
              dialogIsLoading={dialogDeleteCustomerIsLoading}
              handleDialogAction={handleConfirmCustomerDeletion}
              actionButtonText={t('deleteCustomerModal.actionButtonText')}
              dialogTitle={customersToDelete.length === 1 ? t('deleteCustomerModal.simpleTitle') : t('deleteCustomerModal.multipleTitle')}
              handleDialogOpenState={(openState: boolean) => {
                setOpenDeleteCustomerModal(openState)

                if (openState === false) {
                  setErrors({})
                  setErrorInDeletionProcess(false)
                  setCustomerSelectedData((currentSelectedCustomerState: Customer) => {
                    if (currentSelectedCustomerState.registeredFullName) {
                      setCustomersToDelete([])
                      customersToDeleteRef.current = []
                    }

                    return {}
                  })
                }
              }}
            >
              <div className="dialog-content-text">
                {customerSelectedData.registeredFullName ? t('deleteCustomerModal.simpleText', { registeredFullName: customerSelectedData.registeredFullName.toUpperCase() }) : t('deleteCustomerModal.multipleText')}
              </div>
            </Dialog>
          )}
          {openFiltersOptionsModal && (
            <FiltersOptionsModal
              customersData={customersData}
              filtersToApply="beneficiaries"
              handleDialogOpenState={(openState: boolean) => {
                setOpenFiltersOptionsModal(openState)
              }}
              title={t('filtersOptionsModal.beneficiaries.title', { ns: 'common' })}
              actionButtonText={t('filtersOptionsModal.beneficiaries.actionButtonText', { ns: 'common' })}
            />
          )}
        </>
      )}
    </div>
  )
}

export default CustomersManagement
